import React, { useState, useEffect } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, useToast,
} from '@chakra-ui/react';
import { limpiezaCamionesApi, camionesApi, Camion } from '../../../api/serviciosApi';
import EvidenciasInline from '../EvidenciasInline';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const TIPOS_LIMPIEZA = ['Interior', 'Exterior', 'Completa', 'Desinfección'];
const ESTATUS = ['Pendiente', 'En proceso', 'Finalizada'];

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">
      {children}
    </Text>
  </Box>
);

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm">{value || '—'}</Text>
  </Box>
);

const fmt = (v?: string) =>
  v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const FormularioLimpiezaCamion: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();

  const [folio, setFolio] = useState(data?.folio || '');
  const [camionId, setCamionId] = useState(data?.camionId != null ? String(data.camionId) : '');
  const [fecha, setFecha] = useState(data?.fecha ? String(data.fecha).split('T')[0] : new Date().toISOString().split('T')[0]);
  const [responsable, setResponsable] = useState(data?.responsable || '');
  const [tipoLimpieza, setTipoLimpieza] = useState(data?.tipoLimpieza || '');
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [estatus, setEstatus] = useState(data?.estatus || 'Pendiente');
  const [camiones, setCamiones] = useState<Camion[]>([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    camionesApi.list().then((res) => setCamiones(res || [])).catch(() => setCamiones([]));
  }, []);

  const handleGuardar = async () => {
    if (!camionId || !fecha) {
      toast({ title: 'Campos obligatorios', description: 'Selecciona el camión y la fecha.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const body = {
        folio: folio || undefined,
        camionId: Number(camionId),
        fecha,
        responsable: responsable || undefined,
        tipoLimpieza: tipoLimpieza || undefined,
        observaciones: observaciones || undefined,
        estatus,
      };
      if (modo === 'edit') {
        await limpiezaCamionesApi.update(data.id, body);
      } else {
        await limpiezaCamionesApi.create(body);
      }
      toast({ title: modo === 'edit' ? 'Registro actualizado' : 'Registro creado', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'Error al guardar.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  if (modo === 'view') {
    const d = data || {};
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Información del Registro</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="FOLIO" value={d.folio} />
          <Field label="ESTATUS" value={<Badge>{d.estatus}</Badge>} />
          <Field label="CAMIÓN" value={d.camion ? `${d.camion.numeroEconomico}${d.camion.marca ? ' — ' + d.camion.marca : ''}` : d.camionId} />
          <Field label="FECHA" value={fmt(d.fecha)} />
          <Field label="TIPO DE LIMPIEZA" value={d.tipoLimpieza} />
          <Field label="RESPONSABLE" value={d.responsable} />
          <Box gridColumn={{ md: 'span 2' }}>
            <Field label="OBSERVACIONES" value={d.observaciones} />
          </Box>
        </SimpleGrid>
        <EvidenciasInline archivos={d.archivos} />
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Información del Registro</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Folio</FormLabel>
          <Input placeholder="(Automático si se deja vacío)" value={folio} onChange={(e) => setFolio(e.target.value)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Camión</FormLabel>
          <Select placeholder="Selecciona el camión" value={camionId} onChange={(e) => setCamionId(e.target.value)}>
            {camiones.map((c) => (
              <option key={c.id} value={c.id}>{c.numeroEconomico}{c.marca ? ` — ${c.marca}` : ''}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Fecha</FormLabel>
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de limpieza</FormLabel>
          <Select placeholder="Selecciona tipo" value={tipoLimpieza} onChange={(e) => setTipoLimpieza(e.target.value)}>
            {TIPOS_LIMPIEZA.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Responsable</FormLabel>
          <Input placeholder="Nombre del responsable" value={responsable} onChange={(e) => setResponsable(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={estatus} onChange={(e) => setEstatus(e.target.value)}>
            {ESTATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={3} placeholder="Notas u observaciones..." value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioLimpiezaCamion;
